import { html, nothing } from "lit";
import {
  formatContinuityTimestamp,
  hasStructuredContinuity,
  snapshotMatchesSession,
  type PalaceContinuityPoint,
  type PalaceContinuitySnapshot,
  type SessionContinuityPayload,
} from "../controllers/chat-palace-continuity.ts";
import { icons } from "../icons.ts";

export type ChatPalaceContinuityDropdownProps = {
  open: boolean;
  sessionKey: string;
  loading: boolean;
  clearing: boolean;
  mutating: boolean;
  pending: number;
  error: string | null;
  snapshot: PalaceContinuitySnapshot | null;
  draft: string;
  draftCategory: "fact" | "decision";
  onClose: () => void;
  onRefresh: () => void;
  onClearSession: () => void;
  onRemovePoint: (pointId: string) => void;
  onDraftChange: (draft: string) => void;
  onDraftCategoryChange: (category: "fact" | "decision") => void;
  onAddPoint: () => void;
};

let palaceItemPrismSeq = 0;

function renderItemPrism() {
  palaceItemPrismSeq += 1;
  const gradId = `palace-item-prism-${palaceItemPrismSeq}`;
  return html`
    <span class="palace-continuity-dropdown__item-prism" aria-hidden="true">
      <svg viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5 L14.5 14 H1.5 Z" fill="url(#${gradId})" />
        <path d="M8 1.5 L14.5 14 L15.2 12.7 L8.7 0.9 Z" fill="#5b21b6" opacity="0.8" />
        <defs>
          <linearGradient id=${gradId} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stop-color="#6366f1" />
            <stop offset="45%" stop-color="#a855f7" />
            <stop offset="75%" stop-color="#ec4899" />
            <stop offset="100%" stop-color="#fbbf24" />
          </linearGradient>
        </defs>
      </svg>
    </span>
  `;
}

function renderStructuredContinuity(payload: SessionContinuityPayload) {
  const blocks: ReturnType<typeof html>[] = [];

  if (payload.activeTask) {
    blocks.push(html`
      <li class="palace-continuity-dropdown__card palace-continuity-dropdown__card--structured">
        <div class="palace-continuity-dropdown__card-top">
          ${renderItemPrism()}
          <span class="palace-continuity-dropdown__badge">task</span>
        </div>
        <p class="palace-continuity-dropdown__preview">${payload.activeTask.description}</p>
        <p class="palace-continuity-dropdown__path muted">
          Status: ${payload.activeTask.status}
          ${payload.activeTask.workerAgentId ? ` · worker ${payload.activeTask.workerAgentId}` : ""}
        </p>
        ${
          payload.activeTask.findings
            ? html`<p class="palace-continuity-dropdown__preview muted">${payload.activeTask.findings}</p>`
            : nothing
        }
      </li>
    `);
  }

  for (const decision of payload.recentDecisions.slice(0, 5)) {
    blocks.push(html`
      <li class="palace-continuity-dropdown__card palace-continuity-dropdown__card--structured">
        <div class="palace-continuity-dropdown__card-top">
          ${renderItemPrism()}
          <span class="palace-continuity-dropdown__badge">decision</span>
        </div>
        <p class="palace-continuity-dropdown__preview palace-continuity-dropdown__body">${decision.content}</p>
      </li>
    `);
  }

  for (const constraint of payload.constraints.slice(0, 5)) {
    blocks.push(html`
      <li class="palace-continuity-dropdown__card palace-continuity-dropdown__card--structured">
        <div class="palace-continuity-dropdown__card-top">
          ${renderItemPrism()}
          <span class="palace-continuity-dropdown__badge">${constraint.source}</span>
        </div>
        <p class="palace-continuity-dropdown__preview">${constraint.text}</p>
      </li>
    `);
  }

  if (payload.lastWorkerFindings) {
    blocks.push(html`
      <li class="palace-continuity-dropdown__card palace-continuity-dropdown__card--structured">
        <div class="palace-continuity-dropdown__card-top">
          ${renderItemPrism()}
          <span class="palace-continuity-dropdown__badge">worker</span>
        </div>
        <p class="palace-continuity-dropdown__preview palace-continuity-dropdown__body">${payload.lastWorkerFindings}</p>
      </li>
    `);
  }

  return blocks;
}

function renderDropCard(
  point: PalaceContinuityPoint,
  props: Pick<ChatPalaceContinuityDropdownProps, "mutating" | "onRemovePoint">,
) {
  return html`
    <li class="palace-continuity-dropdown__card">
      <div class="palace-continuity-dropdown__card-top">
        <div class="palace-continuity-dropdown__card-meta">
          ${renderItemPrism()}
          <span class="palace-continuity-dropdown__badge">${point.category}</span>
          <time class="muted mono" datetime=${point.timestamp}>
            ${formatContinuityTimestamp(point.timestamp)}
          </time>
        </div>
        <button
          type="button"
          class="btn btn--sm btn--icon palace-continuity-dropdown__remove"
          aria-label="Remove continuity item"
          title="Remove this continuity item"
          ?disabled=${props.mutating}
          @click=${() => props.onRemovePoint(point.id)}
        >
          ${icons.trash}
        </button>
      </div>
      <p class="palace-continuity-dropdown__preview palace-continuity-dropdown__body">
        ${point.body || point.preview}
      </p>
      <p class="palace-continuity-dropdown__path muted mono">${point.path}:${point.startLine}</p>
    </li>
  `;
}

export function renderChatPalaceContinuityDropdown(props: ChatPalaceContinuityDropdownProps) {
  if (!props.open) {
    return nothing;
  }

  const snapshotReady = snapshotMatchesSession(props.snapshot, props.sessionKey);
  const points = snapshotReady ? (props.snapshot?.points ?? []) : [];
  const structured = snapshotReady ? props.snapshot?.structuredContinuity : undefined;
  const memorySnippets = snapshotReady ? (props.snapshot?.memorySnippets ?? []) : [];
  const structuredCount = hasStructuredContinuity(structured) ? 1 : 0;
  const sectionCount = points.length + memorySnippets.length + (structuredCount > 0 ? 1 : 0);
  const showLoading = props.loading || (!snapshotReady && !props.error);
  const draftReady = props.draft.trim().length > 0;
  const busy = props.loading || props.clearing || props.mutating;
  const timedOut = snapshotReady && props.snapshot?.timedOut === true;

  return html`
    <div class="palace-continuity-backdrop" role="presentation" @click=${props.onClose}></div>
    <section
      class="palace-continuity-dropdown"
      role="dialog"
      aria-label="Quantum Palace session continuity"
      @click=${(e: Event) => e.stopPropagation()}
    >
      <header class="palace-continuity-dropdown__header">
        <div class="palace-continuity-dropdown__title-row">
          <span class="palace-continuity-dropdown__icon" aria-hidden="true">${icons.prism}</span>
          <div>
            <h3 class="palace-continuity-dropdown__title">Session continuity</h3>
            <p class="palace-continuity-dropdown__subtitle muted">
              ${
                sectionCount > 0
                  ? `${points.length} drop${points.length === 1 ? "" : "s"} · ${memorySnippets.length} memory hit${memorySnippets.length === 1 ? "" : "s"}`
                  : "No continuity loaded for this session yet"
              }
            </p>
          </div>
        </div>
        <button
          type="button"
          class="btn btn--sm btn--icon"
          aria-label="Close continuity panel"
          @click=${props.onClose}
        >
          ${icons.x}
        </button>
      </header>

      ${
        timedOut
          ? html`
              <p class="palace-continuity-dropdown__status muted">
                Memory search timed out — showing disk session drops. Structured recall may be incomplete.
              </p>
            `
          : nothing
      }
      ${
        showLoading
          ? html`
              <p class="palace-continuity-dropdown__status muted">Loading continuity…</p>
            `
          : props.error
            ? html`<p class="palace-continuity-dropdown__status palace-continuity-dropdown__status--error">${props.error}</p>`
            : sectionCount === 0
              ? html`
                  <p class="palace-continuity-dropdown__status muted">
                    No continuity points yet. Add one below, or wait for agent-end drops and heartbeats.
                  </p>
                `
              : html`
                  <ul class="palace-continuity-dropdown__list">
                    ${
                      hasStructuredContinuity(structured)
                        ? html`
                            <li class="palace-continuity-dropdown__section-label muted">Structured state</li>
                            ${renderStructuredContinuity(structured!)}
                          `
                        : nothing
                    }
                    ${
                      points.length > 0
                        ? html`
                            <li class="palace-continuity-dropdown__section-label muted">Session drops</li>
                            ${points.map((point) => renderDropCard(point, props))}
                          `
                        : nothing
                    }
                    ${
                      memorySnippets.length > 0
                        ? html`
                            <li class="palace-continuity-dropdown__section-label muted">Related memory</li>
                            ${memorySnippets.map(
                              (hit) => html`
                                <li class="palace-continuity-dropdown__card palace-continuity-dropdown__card--memory">
                                  <div class="palace-continuity-dropdown__card-top">
                                    ${renderItemPrism()}
                                    <span class="palace-continuity-dropdown__badge">memory</span>
                                  </div>
                                  <p class="palace-continuity-dropdown__path muted mono">${hit.path}</p>
                                  <p class="palace-continuity-dropdown__preview">${hit.snippet}</p>
                                </li>
                              `,
                            )}
                          `
                        : nothing
                    }
                  </ul>
                `
      }

      <div class="palace-continuity-dropdown__compose">
        <label class="sr-only" for="palace-continuity-draft">New continuity item</label>
        <textarea
          id="palace-continuity-draft"
          class="palace-continuity-dropdown__draft"
          rows="2"
          placeholder="Add a continuity note…"
          .value=${props.draft}
          ?disabled=${busy}
          @input=${(e: Event) =>
            props.onDraftChange((e.currentTarget as HTMLTextAreaElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && draftReady && !busy) {
              e.preventDefault();
              props.onAddPoint();
            }
          }}
        ></textarea>
        <div class="palace-continuity-dropdown__compose-row">
          <select
            class="palace-continuity-dropdown__category"
            aria-label="Continuity category"
            .value=${props.draftCategory}
            ?disabled=${busy}
            @change=${(e: Event) => {
              const value = (e.currentTarget as HTMLSelectElement).value;
              props.onDraftCategoryChange(value === "decision" ? "decision" : "fact");
            }}
          >
            <option value="fact">fact</option>
            <option value="decision">decision</option>
          </select>
          <button
            type="button"
            class="btn btn--sm palace-continuity-dropdown__add"
            ?disabled=${busy || props.pending > 0 || !draftReady}
            title=${props.pending > 0 ? "Write in flight" : ""}
            @click=${props.onAddPoint}
          >
            ${icons.plus}
            <span>${props.mutating || props.pending > 0 ? `Saving…${props.pending > 1 ? ` (${props.pending})` : ""}` : "Add item"}</span>
          </button>
        </div>
      </div>

      <footer class="palace-continuity-dropdown__footer">
        <button
          type="button"
          class="btn btn--sm"
          ?disabled=${props.loading || props.mutating}
          @click=${props.onRefresh}
        >
          Refresh
        </button>
        <button
          type="button"
          class="btn btn--sm btn--danger"
          ?disabled=${props.loading || props.clearing || props.mutating || points.length === 0}
          @click=${props.onClearSession}
        >
          ${props.clearing ? "Clearing…" : "Clear session drops"}
        </button>
      </footer>
    </section>
  `;
}
